import { useState, useEffect } from 'react';
import ImageUpload from '../ImageUpload';
import RichTextEditor from '../RichTextEditor';
import { apiGet, apiPut } from '../api';

export default function CookieSection() {
  const [data, setData] = useState({
    title_line1: '',
    title_line2: '',
    title_line3: '',
    description_paragraph1: '',
    description_paragraph2: '',
    cookie_image1: '',
    cookie_image2: '',
    cookie_image3: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editorMode, setEditorMode] = useState<'simple' | 'rich'>('simple');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const result = await apiGet('cookie.php');
      if (result) {
        setData(result);
        const hasHtml = /<[a-z][\s\S]*>/i.test(result.description_paragraph1 || '') ||
                        /<[a-z][\s\S]*>/i.test(result.description_paragraph2 || '');
        if (hasHtml) {
          setEditorMode('rich');
        }
      }
    } catch (err) {
      setMessage('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      const result = await apiPut('cookie.php', data);
      setMessage(result.success ? 'Salvo com sucesso!' : 'Erro ao salvar');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Erro ao salvar dados');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="p-4 text-gray-500">Carregando...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Seção Bolachas</h2>

      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Título (3 linhas)</h3>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Linha 1</label>
              <input
                type="text"
                value={data.title_line1}
                onChange={(e) => setData({ ...data, title_line1: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="SABOROSA,"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Linha 2</label>
              <input
                type="text"
                value={data.title_line2}
                onChange={(e) => setData({ ...data, title_line2: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="CROCANTE E"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Linha 3</label>
              <input
                type="text"
                value={data.title_line3}
                onChange={(e) => setData({ ...data, title_line3: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="AÇORIANA!"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Descrição</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Modo:</span>
              <button
                type="button"
                onClick={() => setEditorMode('simple')}
                className={`px-3 py-1 text-xs rounded-l-lg border transition-colors ${
                  editorMode === 'simple'
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Simples
              </button>
              <button
                type="button"
                onClick={() => setEditorMode('rich')}
                className={`px-3 py-1 text-xs rounded-r-lg border transition-colors ${
                  editorMode === 'rich'
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Avançado
              </button>
            </div>
          </div>

          {editorMode === 'simple' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Parágrafo 1</label>
                <textarea
                  value={data.description_paragraph1}
                  onChange={(e) => setData({ ...data, description_paragraph1: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-vertical"
                  rows={4}
                  placeholder="Primeiro parágrafo da descrição..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Parágrafo 2</label>
                <textarea
                  value={data.description_paragraph2}
                  onChange={(e) => setData({ ...data, description_paragraph2: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-vertical"
                  rows={4}
                  placeholder="Segundo parágrafo da descrição..."
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  💡 No modo <strong>Simples</strong>, o texto é exibido como está. 
                  Mude para <strong>Avançado</strong> para usar formatação rica (negrito, itálico, links, etc.).
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <RichTextEditor
                value={data.description_paragraph1}
                onChange={(html) => setData({ ...data, description_paragraph1: html })}
                label="Parágrafo 1"
              />
              <RichTextEditor
                value={data.description_paragraph2}
                onChange={(html) => setData({ ...data, description_paragraph2: html })}
                label="Parágrafo 2"
              />
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  ⚠️ No modo <strong>Avançado</strong>, pode usar formatação rica incluindo <strong>hyperlinks</strong>. 
                  Selecione texto e clique no ícone 🔗 para adicionar um link clicável.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <h3 className="font-semibold text-gray-800 mb-3">Imagens das Bolachas</h3>
          
          <ImageUpload
            currentImage={data.cookie_image1}
            onImageChange={(path) => setData({ ...data, cookie_image1: path })}
            label="Bolacha 1 (Esquerda)"
          />

          <ImageUpload
            currentImage={data.cookie_image2}
            onImageChange={(path) => setData({ ...data, cookie_image2: path })}
            label="Bolacha 2 (Centro - Maior)"
          />

          <ImageUpload
            currentImage={data.cookie_image3}
            onImageChange={(path) => setData({ ...data, cookie_image3: path })}
            label="Bolacha 3 (Direita)"
          />
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.includes('sucesso') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
      </button>
    </div>
  );
}
